import { isChunkLoadError, reloadForChunkError } from "@/lib/runtime";

/**
 * Robust dynamic-import wrapper.
 *
 * Strategy when a chunk fails to load (stale Vite optimized-deps cache,
 * deployed hash rotated mid-session, transient network blip):
 *   1. Retry the import a few times with exponential back-off.
 *   2. If still failing, force a one-time hard reload (cache-busted) so the
 *      browser pulls a fresh module graph.
 *   3. Never resolve to a broken chunk — keep the promise pending while the
 *      reload is in flight so React's <Suspense> stays on its fallback
 *      instead of unmounting into an error boundary.
 */
export const lazyWithRetry = <T>(
  importer: () => Promise<T>,
  { retries = 3, baseDelay = 250 }: { retries?: number; baseDelay?: number } = {},
): Promise<T> => {
  const attempt = (n: number): Promise<T> =>
    importer().catch((error) => {
      if (!isChunkLoadError(error)) throw error;

      if (n < retries) {
        const delay = baseDelay * Math.pow(2, n);
        return new Promise<T>((resolve, reject) => {
          setTimeout(() => attempt(n + 1).then(resolve, reject), delay);
        });
      }

      if (reloadForChunkError()) {
        // Hold the promise pending so Suspense keeps rendering its fallback
        // until the reload swaps the document.
        return new Promise<T>(() => {});
      }

      throw error;
    });

  return attempt(0);
};
